import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListContacts,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
  useBulkImportContacts,
  getListContactsQueryKey,
  type Contact,
  type ContactImportSummary,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Users,
  Mail,
  Phone,
  MapPin,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { capitalizeName } from "@/lib/capitalizeName";
import { EmailVerdictBadge, reasonLabel } from "@/components/EmailVerdictBadge";
import { apiErrorData, apiErrorMessage } from "@/lib/apiError";

type FormState = {
  name: string;
  email: string;
  phone: string;
  address: string;
};

const EMPTY_FORM: FormState = { name: "", email: "", phone: "", address: "" };

type CreateContactErrorBody = {
  code?: string;
  verdict?: { status: "valid" | "risky" | "invalid"; reasons: string[] };
};

/**
 * Minimal CSV parser: splits the first non-empty line as headers, then
 * rows. Quoted commas are handled (basic RFC 4180 subset). Returns
 * `null` when the file has no usable rows. Used by the bulk-import
 * dialog so users can paste or upload a CSV with name/email/phone/
 * address columns without us pulling in a heavy CSV dep.
 */
type ParsedCsvRow = {
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
};

function parseCsv(text: string): { rows: ParsedCsvRow[] } | null {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return null;
  const splitLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === "," && !inQuotes) {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map((c) => c.trim());
  };
  const headers = splitLine(lines[0]).map((h) => h.toLowerCase());
  const idx = (key: string) => headers.indexOf(key);
  const nameIdx = idx("name");
  const emailIdx = idx("email");
  const phoneIdx = idx("phone");
  const addressIdx = idx("address");
  if (nameIdx < 0) return null;
  const rows: ParsedCsvRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = splitLine(lines[i]);
    const name = cols[nameIdx]?.trim() ?? "";
    if (!name) continue;
    rows.push({
      name,
      email: emailIdx >= 0 ? cols[emailIdx]?.trim() || null : null,
      phone: phoneIdx >= 0 ? cols[phoneIdx]?.trim() || null : null,
      address: addressIdx >= 0 ? cols[addressIdx]?.trim() || null : null,
    });
  }
  if (rows.length === 0) return null;
  return { rows };
}

export default function Contacts() {
  const queryClient = useQueryClient();
  const { data: contacts, isLoading } = useListContacts();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState<Contact | null>(null);
  const [pendingInvalidVerdict, setPendingInvalidVerdict] = useState<
    CreateContactErrorBody["verdict"] | null
  >(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importSummary, setImportSummary] = useState<ContactImportSummary | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact();
  const deleteMutation = useDeleteContact();
  const bulkImportMutation = useBulkImportContacts();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getListContactsQueryKey() });

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setPendingInvalidVerdict(null);
    setOpen(true);
  }

  function openEdit(c: Contact) {
    setEditing(c);
    setForm({
      name: c.name,
      email: c.email ?? "",
      phone: c.phone ?? "",
      address: c.address ?? "",
    });
    setPendingInvalidVerdict(null);
    setOpen(true);
  }

  async function submit(e: React.FormEvent, allowInvalid = false) {
    e.preventDefault();
    const trimmed = form.name.trim();
    if (!trimmed) {
      toast.error("Name is required");
      return;
    }
    const payload = {
      name: trimmed,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
      ...(allowInvalid ? { allowInvalid: true } : {}),
    };
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, data: payload });
        toast.success("Contact updated");
      } else {
        await createMutation.mutateAsync({ data: payload });
        toast.success("Contact added");
      }
      setOpen(false);
      setPendingInvalidVerdict(null);
      invalidate();
    } catch (err) {
      // The server's "email_invalid" 400 carries the verdict so we can
      // surface a per-reason explanation inline and let the user
      // choose to override with the "Save anyway" button.
      const data = apiErrorData<CreateContactErrorBody>(err);
      if (data?.code === "email_invalid" && data.verdict) {
        setPendingInvalidVerdict(data.verdict);
        return;
      }
      const msg = apiErrorMessage(err, "Failed to save contact");
      toast.error(msg);
    }
  }

  async function confirmDeleteContact() {
    if (!confirmDelete) return;
    try {
      await deleteMutation.mutateAsync({ id: confirmDelete.id });
      toast.success("Contact deleted");
      setConfirmDelete(null);
      invalidate();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to delete contact";
      toast.error(msg);
    }
  }

  async function runImport(allowInvalid: boolean) {
    const parsed = parseCsv(importText);
    if (!parsed) {
      toast.error(
        "Could not read CSV. The first line must be a header row including 'name'.",
      );
      return;
    }
    try {
      const result = await bulkImportMutation.mutateAsync({
        data: { rows: parsed.rows, allowInvalid },
      });
      setImportSummary(result);
      invalidate();
      toast.success(
        `Imported ${result.summary.imported} contact${
          result.summary.imported === 1 ? "" : "s"
        }${result.summary.skipped > 0 ? ` — skipped ${result.summary.skipped}` : ""}.`,
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Bulk import failed"));
    }
  }

  function onFileChosen(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      setImportText(text);
      setImportSummary(null);
    };
    reader.readAsText(file);
  }

  const submitting = createMutation.isPending || updateMutation.isPending;
  const importing = bulkImportMutation.isPending;

  return (
    <div className="flex-1 overflow-auto p-8 bg-background">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Contacts</h1>
            <p className="text-muted-foreground">
              Your customer address book — pick a contact in Compose to fill in
              the recipient details automatically.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setImportText("");
                setImportSummary(null);
                setImportOpen(true);
              }}
              data-testid="button-bulk-import"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button
              onClick={openCreate}
              data-testid="button-add-contact"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : !contacts || contacts.length === 0 ? (
          <div className="text-center py-20 px-4 border-2 border-dashed border-border rounded-xl bg-card">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No contacts yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Add a customer's name, email, phone and address once and reuse it
              from Compose every time you email them.
            </p>
            <Button onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add your first contact
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {contacts.map((c) => (
              <Card
                key={c.id}
                className="group"
                data-testid={`row-contact-${c.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle
                      className="text-base"
                      data-testid={`text-contact-name-${c.id}`}
                    >
                      {capitalizeName(c.name)}
                    </CardTitle>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => openEdit(c)}
                        aria-label={`Edit ${c.name}`}
                        data-testid={`button-edit-contact-${c.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => setConfirmDelete(c)}
                        aria-label={`Delete ${c.name}`}
                        data-testid={`button-delete-contact-${c.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="text-sm space-y-1.5 text-muted-foreground">
                  {c.email && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Mail className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{c.email}</span>
                      {c.validationStatus && (
                        <EmailVerdictBadge
                          verdict={{
                            status: c.validationStatus,
                            reasons: c.validationReasons ?? [],
                          }}
                        />
                      )}
                    </div>
                  )}
                  {c.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{c.phone}</span>
                    </div>
                  )}
                  {c.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span className="whitespace-pre-line">{c.address}</span>
                    </div>
                  )}
                  {!c.email && !c.phone && !c.address && (
                    <span className="italic">No contact details saved</span>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit contact" : "Add contact"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Update this customer's details."
                : "Save a customer so you can re-use them in Compose."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => submit(e, false)} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">Name</Label>
              <Input
                id="contact-name"
                value={form.name}
                onChange={(e) => {
                  setForm((f) => ({ ...f, name: e.target.value }));
                }}
                placeholder="Jane Doe"
                required
                autoFocus
                data-testid="input-contact-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => {
                  setForm((f) => ({ ...f, email: e.target.value }));
                  setPendingInvalidVerdict(null);
                }}
                placeholder="jane@example.com"
                data-testid="input-contact-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">Phone</Label>
              <Input
                id="contact-phone"
                value={form.phone}
                onChange={(e) =>
                  setForm((f) => ({ ...f, phone: e.target.value }))
                }
                placeholder="+1 555 0100"
                data-testid="input-contact-phone"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-address">Address</Label>
              <Textarea
                id="contact-address"
                value={form.address}
                onChange={(e) =>
                  setForm((f) => ({ ...f, address: e.target.value }))
                }
                placeholder="123 Main St, Springfield"
                rows={2}
                data-testid="input-contact-address"
              />
            </div>
            {pendingInvalidVerdict && (
              <div
                className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
                data-testid="banner-invalid-email"
              >
                <div className="flex items-center gap-2 mb-1 font-medium text-destructive">
                  Email looks invalid
                </div>
                <ul className="list-disc list-inside text-destructive/90 text-xs space-y-0.5">
                  {pendingInvalidVerdict.reasons.map((r) => (
                    <li key={r}>{reasonLabel(r)}</li>
                  ))}
                </ul>
                <div className="mt-2 text-xs text-muted-foreground">
                  Save anyway if you trust this address.
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              {pendingInvalidVerdict ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={submitting}
                  onClick={(e) => submit(e, true)}
                  data-testid="button-save-contact-anyway"
                >
                  {submitting && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  Save anyway
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="button-save-contact"
                >
                  {submitting && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {editing ? "Save" : "Add contact"}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk import contacts</DialogTitle>
            <DialogDescription>
              Upload or paste a CSV with a header row. Columns: <code>name</code>{" "}
              (required), <code>email</code>, <code>phone</code>,{" "}
              <code>address</code>. Each email is validated; invalid addresses
              are skipped unless you choose to import anyway.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onFileChosen(f);
                  e.target.value = "";
                }}
                data-testid="input-import-file"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Choose CSV
              </Button>
              <span className="text-xs text-muted-foreground self-center">
                or paste rows below
              </span>
            </div>
            <Textarea
              value={importText}
              onChange={(e) => {
                setImportText(e.target.value);
                setImportSummary(null);
              }}
              rows={8}
              placeholder={`name,email,phone,address\nJane Doe,jane@example.com,+1 555 0100,\nJohn Smith,john@example.com,,`}
              data-testid="input-import-csv"
            />
            {importSummary && (
              <div
                className="rounded-md border bg-muted/30 p-3 text-sm space-y-2"
                data-testid="banner-import-summary"
              >
                <div className="font-medium">
                  {importSummary.summary.imported} of{" "}
                  {importSummary.summary.total} imported
                  {importSummary.summary.skipped > 0 &&
                    ` — ${importSummary.summary.skipped} skipped`}
                </div>
                <div className="text-xs text-muted-foreground">
                  {importSummary.summary.valid} valid •{" "}
                  {importSummary.summary.risky} risky •{" "}
                  {importSummary.summary.invalid} invalid
                </div>
                {importSummary.rows.some((r) => !r.imported) && (
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {importSummary.rows
                      .filter((r) => !r.imported)
                      .map((r) => (
                        <div
                          key={r.index}
                          className="text-xs flex items-center gap-2"
                          data-testid={`row-skipped-${r.index}`}
                        >
                          <span className="text-muted-foreground truncate">
                            {r.name}
                            {r.email ? ` · ${r.email}` : ""}
                          </span>
                          {r.verdict && (
                            <EmailVerdictBadge verdict={r.verdict} />
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setImportOpen(false)}
            >
              Close
            </Button>
            {importSummary && importSummary.summary.skipped > 0 ? (
              <Button
                type="button"
                variant="destructive"
                disabled={importing}
                onClick={() => runImport(true)}
                data-testid="button-import-anyway"
              >
                {importing && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Import skipped anyway
              </Button>
            ) : (
              <Button
                type="button"
                disabled={importing || importText.trim() === ""}
                onClick={() => runImport(false)}
                data-testid="button-run-import"
              >
                {importing && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Validate &amp; import
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this contact?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.name} will be removed from your address book.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteContact}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-contact"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
